import { useRef, useState, type CSSProperties } from 'react';
import { color, workStatusTheme } from '../styles/theme';
import { dateTime, formatBytes, initials, shortDate } from '../lib/format';
import type { WorkFile, WorkStatus } from '../api/client';
import { FILE_ACCEPT, FILE_KINDS, WORK_STATUS_LABEL, fileProblem } from '../api/tickets';
import type { TimelineItem } from '../lib/ticketTimeline';
import { Button, Confirm } from '../components/ui';
import { IconPaperclip } from '../components/icons';

/**
 * The small pieces the ticket page and the New ticket form share: the status
 * pill, one entry on the thread, a file as a chip or as a row in the index,
 * and the picker that gathers files before they go up.
 */

const pill: CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};

/** A text-only button that reads as a link: the back link, a file's name. */
export const linkButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 12.5,
  fontWeight: 700,
  color: color.blue,
  textAlign: 'left',
  fontFamily: 'inherit',
};

const small: CSSProperties = { padding: '5px 11px', fontSize: 12 };

export function StatusPill({ status }: { status: WorkStatus }) {
  const t = workStatusTheme[status];
  return <span style={{ ...pill, background: t.bg, color: t.fg }}>{WORK_STATUS_LABEL[status]}</span>;
}

/** A file on the thread: its name opens the download. */
export function FileChip({ file, onDownload }: { file: WorkFile; onDownload: () => void }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        maxWidth: '100%',
        padding: '4px 10px',
        borderRadius: 10,
        border: `1px solid ${color.line}`,
        background: color.bg,
        color: color.ink,
        fontSize: 12.5,
      }}
    >
      <span style={{ display: 'flex', color: color.muted }}>
        <IconPaperclip size={13} />
      </span>
      <button onClick={onDownload} title="Download" style={{ ...linkButton, overflowWrap: 'anywhere' }}>
        {file.name}
      </button>
      <span style={{ color: color.muted, whiteSpace: 'nowrap' }}>{formatBytes(file.size)}</span>
    </span>
  );
}

/** A file in the index on the rail: who, when, Download, and Remove for whoever may. */
export function FileRow({
  file,
  onDownload,
  onRemove,
  busy,
}: {
  file: WorkFile;
  onDownload: () => void;
  onRemove?: () => void;
  busy?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderTop: `1px solid ${color.line}` }}>
      <span style={{ display: 'flex', color: color.muted, marginTop: 3, flex: 'none' }}>
        <IconPaperclip size={13} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <button onClick={onDownload} title="Download" style={{ ...linkButton, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
          {file.name}
        </button>
        <div style={{ fontSize: 11.5, color: color.muted, marginTop: 2 }}>
          {formatBytes(file.size)} · {file.uploaderName ?? 'a former staff account'} · {shortDate(file.createdAt)}
        </div>
      </div>
      {onRemove && (
        <Confirm
          open={asking}
          title={`Remove ${file.name}?`}
          detail="It goes for everyone on the ticket."
          confirmLabel="Remove"
          align="right"
          onConfirm={() => {
            setAsking(false);
            onRemove();
          }}
          onCancel={() => setAsking(false)}
        >
          <Button variant="ghost" style={small} disabled={busy} onClick={() => setAsking(true)} title="Remove this file">
            Remove
          </Button>
        </Confirm>
      )}
    </div>
  );
}

/** One entry on the thread: an update, with any files that came with it, or a file attached on its own. */
export function ThreadItem({ item, onDownload }: { item: TimelineItem; onDownload: (file: WorkFile) => void }) {
  const who = item.authorName ?? 'A former staff account';
  const files = item.kind === 'comment' ? item.files : [item.file];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '26px minmax(0, 1fr)', gap: 10, alignItems: 'start' }}>
      <span
        aria-hidden
        style={{
          display: 'flex',
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: color.tint2,
          color: color.blueDeep,
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10.5,
          fontWeight: 800,
        }}
      >
        {item.authorName ? initials(item.authorName) : '?'}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>{who}</span>
          <span style={{ fontSize: 11.5, color: color.muted }}>{dateTime(item.at)}</span>
        </div>
        {item.kind === 'comment' ? (
          <p style={{ fontSize: 14, lineHeight: '21px', color: color.ink, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '3px 0 0' }}>
            {item.body}
          </p>
        ) : (
          <p style={{ fontSize: 13, color: color.muted, margin: '3px 0 0' }}>Attached a file</p>
        )}
        {files.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {files.map((f) => (
              <FileChip key={f.id} file={f} onDownload={() => onDownload(f)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Files gathered before they go up: an Attach button over a hidden input,
 * a chip per file with ×, and the reason when one was refused. The checks
 * are the API's own limits, so most refusals never leave the browser.
 * `have` is how many files the ticket already holds.
 */
export function AttachPicker({
  files,
  onChange,
  have,
  disabled,
}: {
  files: ReadonlyArray<File>;
  onChange: (next: File[]) => void;
  have: number;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);

  function pick(list: FileList | null) {
    if (!list) return;
    const next = [...files];
    let refused: string | null = null;
    for (const f of Array.from(list)) {
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      const why = fileProblem(f, have + next.length);
      if (why) {
        refused = why;
        continue;
      }
      next.push(f);
    }
    setProblem(refused);
    onChange(next);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Button
          variant="ghost"
          style={small}
          disabled={disabled}
          onClick={() => input.current?.click()}
          title={`Up to 10 MB a file · ${FILE_KINDS}`}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconPaperclip size={13} /> Attach
          </span>
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          accept={FILE_ACCEPT}
          aria-label="Attach files"
          style={{ display: 'none' }}
          onChange={(e) => {
            pick(e.target.files);
            e.target.value = '';
          }}
        />
        {files.map((f, i) => (
          <span
            key={`${f.name}-${f.size}-${i}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              maxWidth: '100%',
              padding: '3px 4px 3px 10px',
              borderRadius: 10,
              border: `1px solid ${color.line}`,
              background: color.tint,
              color: color.ink,
              fontSize: 12.5,
            }}
          >
            <span style={{ overflowWrap: 'anywhere' }}>{f.name}</span>
            <span style={{ color: color.muted, whiteSpace: 'nowrap' }}>{formatBytes(f.size)}</span>
            <button
              type="button"
              aria-label={`Do not attach ${f.name}`}
              disabled={disabled}
              onClick={() => onChange(files.filter((_, j) => j !== i))}
              style={{ ...linkButton, color: color.muted, fontSize: 15, lineHeight: 1, padding: '0 5px' }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {problem && (
        <p role="alert" style={{ fontSize: 12.5, color: color.red, margin: 0 }}>
          {problem}
        </p>
      )}
    </div>
  );
}
